import * as React from 'react';
import type { ScaleLinear } from 'd3-scale';

import type { XScale } from './scales';
import type { ChartDimensions, YAxisSide } from './types';

/** Scales and geometry shared by every primitive inside one chart. */
export interface ChartContextValue {
  dimensions: ChartDimensions;
  xScale: XScale;
  /** Left y-scale — the default axis */
  yScale: ScaleLinear<number, number>;
  /** Right y-scale, present only when a series declares `axis: 'right'` */
  yScaleRight?: ScaleLinear<number, number>;
}

const ChartContext = React.createContext<ChartContextValue | null>(null);

export const ChartContextProvider = ChartContext.Provider;

/** Reads the enclosing chart geometry — throws outside a `ChartRoot`. */
export function useChartContext(): ChartContextValue {
  const value = React.useContext(ChartContext);
  if (!value) throw new Error('Chart primitives must be rendered inside <ChartRoot>');
  return value;
}

/** Resolves the y-scale for an axis side, falling back to the left scale. */
export function yScaleFor(
  context: ChartContextValue,
  axis: YAxisSide
): ScaleLinear<number, number> {
  return axis === 'right' ? (context.yScaleRight ?? context.yScale) : context.yScale;
}
